/**
 * 角色路由 guard —— 路由配置层统一拦截
 *
 * 用法：
 *   <Route path="/reports" element={
 *     <RouteGuard allowedRoles={['admin', 'director', 'manager']}>
 *       <ReportsPage />
 *     </RouteGuard>
 *   } />
 *
 * 不在 allowedRoles 内的用户立即 <Navigate> 到 redirectTo，不触发任何 API。
 * 之前在页面组件顶部内联 Navigate 的写法有两个坑：
 *   1. 容易漏（新增 admin-only 页面忘记加 guard）
 *   2. 已经触发了 useQuery 之类的副作用 → sales 进 /reports 弹 12 条 403 toast
 * 改成路由层 guard，渲染前就拦死。
 */
import { Navigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { message } from 'antd';
import { useAuthStore } from '../store/auth';
// v1.5 阶段 4 P4：RouteGuard 加 requirePermissions（兼容现有 allowedRoles）
import { usePermissions } from '../lib/permissions';

interface Props {
  // v1.2 Batch 3-B：加 academic / academic_director
  allowedRoles?: Array<'admin' | 'director' | 'manager' | 'sales' | 'academic' | 'academic_director'>;
  /** bug收尾批#5：按 director_type 维度额外放行（财务总监程华庆=academic_director+finance，不在 allowedRoles 角色白名单内也放行）。 */
  allowDirectorTypes?: Array<'data' | 'finance'>;
  /** v1.5 P4：要求的权限点（AND 数组=全部需要 / 单 code 同样语义） */
  requirePermissions?: string | string[];
  /** v1.5 P4：要求任一权限点（OR） */
  requireAnyPermissions?: string[];
  redirectTo?: string;
  children: React.ReactNode;
}

export function RouteGuard({
  allowedRoles, allowDirectorTypes, requirePermissions, requireAnyPermissions,
  redirectTo = '/dashboard', children,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const perms = usePermissions();
  // v1.2-rc2 P1-3：越权时给一条 toast
  // v1.5 P4：拒绝逻辑 = 不满足 allowedRoles（如有）OR 不满足 requirePermissions（如有）
  // bug收尾批#5：allowedRoles 命中 OR director_type 命中(财务总监维度) 即放行；两者皆不中才按角色拒。
  const roleDenied = !!user && !!allowedRoles
    && !allowedRoles.includes(user.role as any)
    && !(allowDirectorTypes && allowDirectorTypes.includes((user as any).directorType));
  const permDenied = !!user && (
    (requirePermissions && !perms.has(requirePermissions))
    || (requireAnyPermissions && !perms.hasAny(requireAnyPermissions))
  );
  const denied = !!(roleDenied || permDenied);
  const shown = useRef(false);
  useEffect(() => {
    if (denied && !shown.current) {
      shown.current = true;
      // v1.2-rc2 #7：文案随 redirectTo 真实落点变化，不能写死"工作台"
      // 之前 academic 被踢到 /academic/students 却 toast "已返回工作台" → 误导
      const dest =
        redirectTo === '/academic/dashboard' ? '教务工作台'
        : redirectTo === '/academic/students' ? '学员对接'
        : redirectTo === '/dashboard' ? '工作台'
        : '首页';
      message.warning(`无权访问该页面，已返回${dest}`);
    }
  }, [denied, redirectTo]);

  if (!user) return <Navigate to="/login" replace />;
  if (denied) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}
